import { Award } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { useHideCourseProgress } from "@/hooks/useHideCourseProgress";
import { courseCompletionQuizPath } from "@/lib/courseCompletionQuiz";
import {
  formatRemainingTime,
  nextAttemptIn,
} from "@/lib/courseCompletionQuizFormat";
import {
  findCourseFinalQuizQuestionCount,
  isCourseFinalQuizUnlocked,
} from "@/lib/courseFinalQuiz";
import type { CourseCompletionQuizSummary } from "@/types/courseCompletionQuiz";

interface QuizMenuStanding {
  attempts?: string;
  stateLabel?: string;
  stateClassName?: string;
}

const attemptCount = (
  quiz: CourseCompletionQuizSummary
): string | undefined => {
  if (quiz.attemptsUsed == null || !quiz.maxAttempts) return undefined;

  return `Versuch ${quiz.attemptsUsed} von ${quiz.maxAttempts}`;
};

/**
 * Attempt count and outcome for the course menu, in the same words as the
 * dashboard card. The count is shown before the first attempt as well. A
 * passed quiz names the outcome only.
 */
const quizMenuStanding = (
  quiz: CourseCompletionQuizSummary | null | undefined
): QuizMenuStanding | null => {
  if (!quiz || quiz.status === "locked") return null;

  if (quiz.status === "startable") {
    const attempts = attemptCount(quiz);
    if (!attempts) return null;

    return { attempts };
  }

  if (quiz.status === "in_progress") {
    return {
      attempts: attemptCount(quiz),
      stateLabel: "in Bearbeitung",
      stateClassName: "text-info-foreground",
    };
  }

  if (quiz.status === "passed") {
    return {
      stateLabel: "Bestanden",
      stateClassName: "text-success-foreground",
    };
  }

  return {
    attempts: attemptCount(quiz),
    stateLabel: "Nicht bestanden",
    stateClassName: "text-error-foreground",
  };
};

const renderStandingLabel = (standing: QuizMenuStanding) => {
  if (!standing.stateLabel) return null;

  const label = (
    <span className={standing.stateClassName}>{standing.stateLabel}</span>
  );

  if (!standing.attempts) return label;

  return (
    <>
      {" · "}
      {label}
    </>
  );
};

const questionCountLabel = (questionCount: number | null) => {
  if (questionCount === null) return null;

  return `${questionCount} Fragen`;
};

const metaJoin = (
  questionCount: number | null,
  standing: QuizMenuStanding | null
) => {
  if (questionCount === null || !standing) return null;
  if (!standing.attempts && !standing.stateLabel) return null;

  return " · ";
};

const renderStanding = (standing: QuizMenuStanding | null) => {
  if (!standing) return null;

  return (
    <>
      {standing.attempts}
      {renderStandingLabel(standing)}
    </>
  );
};

/** How long the 24-hour lock still lasts; nothing once a new attempt is allowed. */
const timeUntilNextTry = (
  quiz: CourseCompletionQuizSummary | null | undefined,
  now: Date
): string | null => {
  if (quiz?.status !== "waiting" || !quiz.nextAttemptAt) return null;

  const remaining = formatRemainingTime(quiz.nextAttemptAt, now);
  if (!remaining) return null;

  return nextAttemptIn(remaining);
};

/** Same left edge as the question count, directly under it. */
const renderNextTry = (timeLabel: string | null) => {
  if (!timeLabel) return null;

  return (
    <span className="mt-0.5 block text-[13px] leading-tight text-muted-foreground">
      {timeLabel}
    </span>
  );
};

const renderQuizMeta = (
  questionCount: number | null,
  quiz: CourseCompletionQuizSummary | null | undefined
) => {
  const standing = quizMenuStanding(quiz);
  if (questionCount === null && !standing) return null;

  return (
    <span className="block text-[13px] text-muted-foreground">
      {questionCountLabel(questionCount)}
      {metaJoin(questionCount, standing)}
      {renderStanding(standing)}
    </span>
  );
};

/**
 * Last TOC entry for the course completion quiz. Only learners with an
 * enrollment see it, and only once they have worked through enough of the
 * course. It opens the same page as the dashboard: the introduction, the
 * open run, or the result.
 */
const CourseFinalQuizEntry = () => {
  const enrollmentId = useCourseDetailStore((s) => s.enrollmentId);
  const progressPercent = useCourseDetailStore((s) => s.progressPercent);
  const nodeIndex = useCourseDetailStore((s) => s.nodeIndex);
  const quiz = useCourseDetailStore(
    (s) => s.enrollment?.courseCompletionQuiz ?? null
  );
  const navigate = useNavigate();
  const hideProgress = useHideCourseProgress();

  if (
    !isCourseFinalQuizUnlocked(
      Boolean(enrollmentId) && !hideProgress,
      progressPercent
    )
  ) {
    return null;
  }

  const questionCount = findCourseFinalQuizQuestionCount(nodeIndex);
  const quizPassed = quiz?.status === "passed";
  const nextTryIn = timeUntilNextTry(quiz, new Date());

  const openCourseCompletionQuiz = () => {
    if (!enrollmentId) return;

    const status = quiz?.status ?? "startable";
    navigate(courseCompletionQuizPath(enrollmentId, status));
  };

  return (
    <div className="border-t border-gray-200">
      <button
        type="button"
        onClick={openCourseCompletionQuiz}
        className="flex w-full cursor-pointer items-center gap-3 px-5 py-2.5 text-left transition-colors hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none"
      >
        {/* Hidden "0", same font as the module numbers, so the medal column
            is one digit wide. The medal is centered on it. */}
        <span
          aria-hidden="true"
          className={cn(
            "relative flex shrink-0 items-center justify-center self-stretch font-bold leading-none tabular-nums text-[calc(18px*1.375*2)] before:invisible before:content-['0']",
            quizPassed ? "text-success" : "text-secondary"
          )}
        >
          <Award
            className="absolute top-1/2 left-1/2 size-[0.72em] -translate-x-1/2 -translate-y-1/2"
            strokeWidth={2.75}
          />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block wrap-break-word text-[18px] font-bold leading-snug text-foreground">
            Kursabschlussquiz
          </span>
          {renderQuizMeta(questionCount, quiz)}
          {renderNextTry(nextTryIn)}
        </span>
      </button>
    </div>
  );
};

export default CourseFinalQuizEntry;
